import * as React from 'react';
import { Typography, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    leaders: {
      width: '100%',
      margin: 0,
      padding: 0,
      overflowX: 'hidden',
      listStyle: 'none',
    },
    line: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: (props: { isWrap: boolean }) => (props.isWrap ? undefined : 'nowrap'),
      padding: theme.spacing(1, 0),
    },
    key: {
      display: 'flex',
      flex: '0 1 300px',
      minWidth: (props: { isWrap: boolean }) => (props.isWrap ? undefined : '300px'),
    },
    dots: {
      position: 'relative',
      flexGrow: 1,
      marginLeft: '3px',
      overflow: 'hidden',
      '&:after': {
        position: 'absolute',
        bottom: '1px',
        float: 'left',
        width: 0,
        whiteSpace: 'nowrap',
        fontSize: '7px',
        content:
          '"........................................"' +
          '"........................................"' +
          '"........................................"' +
          '"........................................"' +
          '"........................................"' +
          '"........................................"' +
          '"........................................"' +
          '"........................................"' +
          '"........................................"' +
          '"........................................"' +
          '"........................................"',
      },
    },
    value: {
      flex: '1 1 60%',
      marginLeft: '3px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    rect: {
      marginRight: theme.spacing(1),
    },
  })
);

/**
 * @summary Тип, представляющий строку для "лесенки".
 *
 * @property key - Текст в левом краю лесенки.
 * @property value - Значение в правом краю лесенки.
 * @property id - Используется, как React Key для соответствующей строки.
 * @property color - Цвет маркера перед ключом.
 */
export type DotLeadersItem = {
  key: string | JSX.Element | JSX.Element[];
  value: string | JSX.Element | JSX.Element[];
  id?: string;
  color?: string;
};

type Props = {
  items: DotLeadersItem[];
  isWrap?: boolean;
};

const rectSize = 15;

/**
 * @summary Компонент, который перечисляет информацию через лесенку из точек.
 *
 * @param items - Массив из специальных объектов, который будет
 * итерироваться через точки.
 *
 * @see https://en.wikipedia.org/wiki/Leader_(typography)
 */
export const DotLeaders: React.FC<Props> = ({ items, isWrap = false }) => {
  const classes = useStyles({ isWrap });

  return (
    <ul className={classes.leaders}>
      {items.map((item, index) => (
        <li className={classes.line} key={item.id || index}>
          <div className={classes.key}>
            {item.color && (
              <div className={classes.rect}>
                <svg width={rectSize} height={rectSize}>
                  <rect fill={item.color} width={rectSize} height={rectSize} />
                </svg>
              </div>
            )}
            <Typography variant="body2" component="span">
              {item.key}
            </Typography>
            <span className={classes.dots} />
          </div>
          <Typography variant="body2" component="div" className={classes.value}>
            {item.value}
          </Typography>
        </li>
      ))}
    </ul>
  );
};
